import { router, useFocusEffect } from "expo-router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import WiresCharts from "./charts/WiresCharts";

import TitleXl from "@/components/TitleXl";
import AccountsList from "@/components/management/AccountsList";
import FeesList from "@/components/management/FeesList";
import TransactionsList from "@/components/management/TransactionsList";
import { sendRequest } from "@/utils/ApiUtil";

interface AccountsProps {
  data: any;
}

type Account = {
  asset_type_id: string;
  available_balance: number;
  current_balance: number;
  customer_id: string;
  id: string;
  product_id: string;
  product_type: string;
  status: string;
  fee: {
    id: string;
    ach: string;
    swift: string;
    swiftIn: string;
    domestic: string;
    domesticIn: string;
    processingFee: number;
    conversionFee: number;
  };
};

const initialAccount: Account = {
  asset_type_id: "",
  available_balance: 0,
  current_balance: 0,
  customer_id: "",
  id: "",
  product_id: "",
  product_type: "",
  status: "",
  fee: {
    id: "",
    ach: "",
    swift: "",
    swiftIn: "",
    domestic: "",
    domesticIn: "",
    processingFee: 0,
    conversionFee: 0,
  },
};

export default function Accounts(props: AccountsProps) {
  const [accounts, setAccounts] = useState([]);
  const [currentAccountId, setCurrentAccountId] = useState("");
  const [currentAccount, setCurrentAccount] = useState<Account>(initialAccount);
  const { t } = useTranslation();

  useFocusEffect(
    React.useCallback(() => {
      getAccounts();
      return () => {
        setCurrentAccountId("");
      };
    }, [])
  );

  useEffect(() => {
    const account = accounts.find((account) => account.id === currentAccountId);
    setCurrentAccount(account);
  }, [accounts, currentAccountId]);

  const getAccounts = async () => {
    try {
      const request = await sendRequest(
        "get",
        "/admin/accounts",
        {
          customerId: props.data.id,
          page: 0,
          pageSize: 100,
        },
        true,
        true
      );
      setAccounts(request);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <View className="w-full bg-white1">
      {currentAccountId ? (
        <View className="w-full p-2">
          <View className="w-full items-center">
            <TitleXl
              text={`${t("common:terms.accountid")}: ${currentAccountId}`}
            />
          </View>
          <FeesList
            accountData={currentAccount}
            syncData={() => {
              getAccounts();
            }}
          />
          <TransactionsList accountId={currentAccountId} />
          <WiresCharts />
        </View>
      ) : (
        <AccountsList
          data={accounts || []}
          selectAccount={setCurrentAccountId}
        />
      )}
    </View>
  );
}
